/**
 * Drone D1 #322 — the COP-side lifecycle mission panel ({kind:'mission'} SidePanel arm). Mirrors
 * the IncidentManagementSection shape: a header (operator / asset / status), the dispatch terms,
 * a lifecycle timeline (requested → accepted → confirmed → enroute → flown → billed → paid), and
 * the LIVE drone feed embedded inline while the mission is enroute / flown. The mission's marketplace
 * status sits BESIDE its IMS resource-lifecycle band (Phase 4d §I.9) so Logistics reads a tasked
 * drone in the same language as every other resource. Phase 4d also adds the requestor/Logistics
 * RELEASE (demobilize) control — the one mutation on this otherwise read-surface panel — closing the
 * "I phone the operator to say you're released" gap; the marketplace transition controls still live
 * in DroneMissionsPanel. Colour discipline (council Law 1): slate furniture + the droneMissionUi badge
 * palette; static markers (no pulsing chrome beyond the player's own LIVE badge).
 */
import { useEffect, useState } from 'react';
import { isMissionFeedLive } from '@tsr/shared-types';
import { demobilizeDroneMission, type DroneMissionView } from './api';
import { aoFromCapabilities, capabilitiesSummary, missionBillingLabel, missionStatusBadge, missionTermsSummary } from './droneMissionUi';
import { lifecycleStateStyle } from './styles/lifecycleStates';
import { FlyToIncidentButton } from './FlyToIncidentButton';
import { IncidentDronesPanel } from './IncidentDronesPanel';
import { MissionFeedEmbed } from './MissionFeedEmbed';

/** Compact, locale-free render of an ISO `…THH:MM:SSZ` stamp (deterministic for tests). */
function fmtTime(iso: string | null): string | null {
  if (!iso) return null;
  return iso.replace('T', ' ').replace('Z', ' UTC');
}

interface Milestone {
  label: string;
  at: string | null;
}

export function DroneMissionDetailBody({
  mission,
  onFlyToIncident,
  onSelectMission,
}: {
  mission: DroneMissionView;
  /** #388 — fly the COP to the linked incident's centroid (mirrors CommunityBody). */
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** Phase 4e — open a SIBLING drone's mission from the "drones on this incident" list (the
   *  Situation-Unit feed click-through, §I.2). The current mission's own feed is already embedded
   *  above, so its row stays static. */
  onSelectMission?: (m: DroneMissionView) => void;
}): JSX.Element {
  // Phase 4d — a local release OVERLAY so the panel reflects a demobilize immediately without
  // threading state up through the SidePanel selection. Reset whenever a different mission opens.
  const [released, setReleased] = useState<DroneMissionView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  useEffect(() => {
    setReleased(null);
    setConfirming(false);
    setReleaseError(null);
  }, [mission.id]);
  const view = released ?? mission;

  const badge = missionStatusBadge(view.status);
  const terms = missionTermsSummary(mission);
  const live = isMissionFeedLive(view.status);
  // Phase 4d — the IMS resource-lifecycle band rendered BESIDE the marketplace badge; null = no
  // resource line (declined/cancelled). Reuses the locked lifecycleStates palette.
  const band = view.lifecycle_band ? lifecycleStateStyle(view.lifecycle_band) : null;
  // Release is offered only while the asset is actively in the field (enroute / flown) and not
  // already released; the server re-enforces this guard on the PATCH.
  const releasable =
    (view.status === 'enroute' || view.status === 'flown') && !view.demobilized_at;

  async function handleRelease(): Promise<void> {
    if (isReleasing) return;
    setIsReleasing(true);
    setReleaseError(null);
    try {
      const updated = await demobilizeDroneMission(view.id);
      setReleased(updated);
      setConfirming(false);
    } catch {
      setReleaseError('Could not release the drone — try again.');
    } finally {
      setIsReleasing(false);
    }
  }

  const milestones: Milestone[] = [
    { label: 'Requested', at: mission.requested_at },
    { label: 'Accepted', at: mission.accepted_at },
    { label: 'Confirmed', at: mission.confirmed_at },
    { label: 'Enroute', at: mission.enroute_at },
    { label: 'Flown', at: mission.flown_at },
  ];

  return (
    <div className="px-4 py-4 space-y-4" data-testid="drone-mission-detail-body">
      {/* Header */}
      <div className="border-l-2 border-cyan-500/60 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Drone Mission</p>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold truncate">{mission.operator_name ?? 'Operator'}</h2>
          <div className="shrink-0 flex items-center gap-1">
            {band && (
              <span
                data-testid="mission-detail-lifecycle-band"
                className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                style={{ backgroundColor: `${band.color}33`, color: band.color }}
                title={`IMS resource lifecycle: ${band.label}`}
              >
                {band.label}
              </span>
            )}
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}
              data-testid="mission-detail-status"
            >
              {badge.label}
            </span>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-0.5">
          {mission.asset_name ?? 'Asset TBD'} · {capabilitiesSummary(mission.requested_capabilities)}
        </p>
      </div>

      {/* Live feed — only while enroute / flown (the server re-gates this too). */}
      {live && (
        <section data-testid="mission-detail-feed">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Live feed</h3>
          <MissionFeedEmbed mission={mission} />
        </section>
      )}

      {/* Dispatch terms (NULL until the operator accepts-with-terms). */}
      {terms && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Dispatch terms</h3>
          <p className="text-sm text-slate-200" data-testid="mission-detail-terms">
            {terms}
          </p>
        </section>
      )}

      {/* Lifecycle timeline */}
      <section className="border-t border-slate-800 pt-3">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Lifecycle</h3>
        <ol className="space-y-1.5">
          {milestones.map((ms) => {
            const reached = ms.at != null;
            return (
              <li key={ms.label} className="flex items-baseline gap-2 text-[12px]">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${
                    reached ? 'bg-cyan-400' : 'bg-slate-600'
                  }`}
                />
                <span className={reached ? 'text-slate-200' : 'text-slate-500'}>{ms.label}</span>
                {reached && (
                  <span className="ml-auto font-mono text-[10px] text-slate-500">{fmtTime(ms.at)}</span>
                )}
              </li>
            );
          })}
        </ol>
        {mission.cancelled_at && (
          <p className="mt-2 text-[11px] text-slate-400">
            Cancelled · {fmtTime(mission.cancelled_at)}
          </p>
        )}
        {mission.declined_reason && (
          <p className="mt-2 text-[11px] text-slate-400">Declined: {mission.declined_reason}</p>
        )}
      </section>

      {/* Phase 4d — requestor/Logistics RELEASE (demobilize): the one mutation on this panel.
          Frees the asset for re-tasking + flips the IMS band to demobilized, without phoning the
          operator. Two-click (non-blocking) confirm. Shown while releasable or once released. */}
      {(releasable || view.demobilized_at) && (
        <section className="border-t border-slate-800 pt-3" data-testid="mission-release-section">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Resource release</h3>
          {view.demobilized_at ? (
            <p className="text-[11px] text-slate-400" data-testid="mission-released-note">
              Released · {fmtTime(view.demobilized_at)}
              {view.demobilization_reason ? ` · ${view.demobilization_reason}` : ''}
            </p>
          ) : confirming ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={isReleasing}
                onClick={() => void handleRelease()}
                data-testid="mission-release-confirm"
                className="text-[11px] rounded border border-rose-500/60 px-2 py-0.5 text-rose-200 hover:border-rose-400 hover:text-rose-100 disabled:opacity-50"
              >
                {isReleasing ? 'Releasing…' : 'Confirm release'}
              </button>
              <button
                type="button"
                disabled={isReleasing}
                onClick={() => setConfirming(false)}
                className="text-[11px] rounded border border-slate-600/60 px-2 py-0.5 text-slate-300 hover:border-slate-400 hover:text-slate-100 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              data-testid="mission-release-button"
              className="text-[11px] rounded border border-slate-600/60 px-2 py-0.5 text-slate-300 hover:border-rose-400 hover:text-rose-200"
            >
              Release / Demobilize
            </button>
          )}
          {releaseError && (
            <p className="mt-1 text-[11px] text-rose-300" data-testid="mission-release-error">
              {releaseError}
            </p>
          )}
        </section>
      )}

      {/* Billing / linked incident */}
      <section className="border-t border-slate-800 pt-3 space-y-2 text-[11px] text-slate-400">
        <p>
          {mission.billable ? 'Billable' : 'No charge'}
          {mission.ledger ? ` · ${missionBillingLabel(mission.ledger.status)}` : ''}
        </p>
        {/* #388 incident back-ref — name the linked incident + fly the COP to it
            (the reverse of #345's incident→drones view). */}
        {mission.incident_id && (
          <div className="space-y-1.5" data-testid="mission-linked-incident">
            <p className="text-slate-300">
              Linked incident:{' '}
              <span className="font-medium text-slate-100">
                {mission.incident_name ?? 'a tracked incident'}
              </span>
            </p>
            {mission.incident_lat != null && mission.incident_lon != null && (
              <FlyToIncidentButton
                lat={mission.incident_lat}
                lon={mission.incident_lon}
                onFlyToIncident={onFlyToIncident}
              />
            )}
          </div>
        )}
      </section>

      {/* Multi-drone persistent coverage (#345) — every drone tasked to this incident + a
          "request another drone" affordance to stack continuous coverage across operators. */}
      {mission.incident_id && (
        <IncidentDronesPanel
          incidentId={mission.incident_id}
          currentMissionId={mission.id}
          requestAo={aoFromCapabilities(mission.requested_capabilities)}
          aoLabel="this incident"
          onSelectMission={onSelectMission}
        />
      )}
    </div>
  );
}
